import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BuildingSelectItem } from 'src/components/BuildingSelect/BuildingSelect';
import { TownSelectItem } from 'src/components/TownSelect/TownSelect';
import {
  getAdminBuildingSelectItem,
  getAdminState,
  getAdminTownSelectItem,
} from 'src/store/admin/selectors';
import { getFloorsState } from 'src/store/floors/selectors';
import { changeFloor, addFloor } from 'src/store/floors/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface AdministratorFloorFormValues {
  id: number | undefined;
  title: string;
  town: TownSelectItem | undefined;
  building: BuildingSelectItem | undefined;
  enabled: boolean;
  disabledReason: string;
  isTestData: boolean;
  isParking: boolean;
}

const defaultFormValues: AdministratorFloorFormValues = {
  id: undefined,
  title: '',
  town: undefined,
  building: undefined,
  enabled: true,
  disabledReason: '',
  isTestData: false,
  isParking: false,
};

export interface AdministratorFloorFormProps {
  children?: ReactNode;
}

export const AdministratorFloorForm: FC<AdministratorFloorFormProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const { selectedFloorId } = useSelector(getAdminState);
  const town = useSelector(getAdminTownSelectItem);
  const building = useSelector(getAdminBuildingSelectItem);
  const { floorsById } = useSelector(getFloorsState);

  const save = useCallback(
    async (values: AdministratorFloorFormValues) => {
      if (!values.town?.value.townId || !values.building?.value.buildingId)
        return false;

      const params = {
        townId: values.town.value.townId,
        buildingId: values.building.value.buildingId,
        title: values.title,
        ownEnabled: values.enabled,
        ownDisabledReason: values.disabledReason,
        testData: values.isTestData,
        isParking: values.isParking,
      };

      if (values.id) {
        await dispatch(changeFloor({ floorId: values.id, ...params }));
      } else {
        await dispatch(addFloor(params));
      }

      return true;
    },
    [dispatch],
  );

  const initialValues = useMemo((): AdministratorFloorFormValues => {
    if (!selectedFloorId)
      return {
        ...defaultFormValues,
        town,
        building,
      };

    const floor = floorsById.get(selectedFloorId);

    if (floor)
      return {
        id: floor.floorId,
        town,
        building,
        title: floor.title,
        enabled: floor.ownEnabled ?? false,
        disabledReason: floor.ownDisabledReason ?? '',
        isTestData: floor.testData ?? false,
        isParking: floor.isParking ?? false,
      };

    return {
      ...defaultFormValues,
      town,
      building,
    };
  }, [building, floorsById, selectedFloorId, town]);

  return (
    <Form onSubmit={save} initialValues={initialValues}>
      {() => children}
    </Form>
  );
};
